// What kind of file an incident attachment is, for validation (before upload)
// and for picking its preview (after). One definition for the whole domain: the
// report modal, the management drawer and the Post-to-Jira modal each used to
// carry their own `isVideoName` regex, so widening the accepted kinds to
// documents and audio fixed the gate in one place and left two previews
// rendering a .pdf/.docx/.mp3 as a broken `<img>`.

export type AttachmentKind = 'image' | 'video' | 'audio' | 'document';

const VIDEO_EXTENSIONS = /\.(mp4|mov|avi|mkv|m4v)$/i;
const AUDIO_EXTENSIONS = /\.(mp3|m4a|aac|wav|ogg|oga|opus|amr|weba)$/i;
const DOCUMENT_EXTENSIONS =
  /\.(pdf|docx?|xlsx?|pptx?|txt|csv|rtf|odt|ods|odp|md|eml|msg)$/i;

export const isVideoName = (name: string): boolean =>
  VIDEO_EXTENSIONS.test(name);

export const isAudioName = (name: string): boolean =>
  AUDIO_EXTENSIONS.test(name);

export const isDocumentName = (name: string): boolean =>
  DOCUMENT_EXTENSIONS.test(name);

const DOCUMENT_MIME_PREFIXES = [
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument',
  'application/vnd.oasis.opendocument',
  'application/rtf',
  'text/',
];

const isDocumentMime = (type: string): boolean =>
  DOCUMENT_MIME_PREFIXES.some((prefix) => type.startsWith(prefix));

type FileLike = { name: string; type: string };

// The browser's `type` is authoritative when present but is routinely empty
// (files dragged from some mail clients, .msg/.eml, phone voice notes), so the
// extension is the fallback. `.webm` is deliberately absent from both extension
// lists: it is a container used for BOTH audio and video, so it is classified by
// its MIME only and otherwise rejected rather than silently mis-capped.
export const resolveAttachmentKind = (
  file: FileLike
): AttachmentKind | null => {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (isDocumentMime(file.type)) return 'document';
  if (isDocumentName(file.name)) return 'document';
  if (isVideoName(file.name)) return 'video';
  if (isAudioName(file.name)) return 'audio';
  return null;
};

// Stored attachments carry a name and an optional contentType — enough to pick a
// preview. Anything unrecognised is treated as a document (an icon), never as an
// image, so a preview can no longer render as a broken thumbnail.
export const previewKindFor = (attachment: {
  name: string;
  contentType?: string;
}): AttachmentKind =>
  resolveAttachmentKind({
    name: attachment.name,
    type: attachment.contentType ?? '',
  }) ?? 'document';
